/** Lentes: ver o modelo por regras, não por uma dimensão só.
 *
 *  O que já havia e porque não chegava. O "Colorir por" pinta o modelo
 *  inteiro por uma dimensão (classe, tipo, material) e dá uma legenda,
 *  mas não esconde nada. O Filtro esconde e isola, mas não pinta. Uma
 *  pergunta corrente de coordenação, "mostra-me as paredes a vermelho,
 *  as lajes a azul e esconde o resto", não se responde com nenhum dos
 *  dois.
 *
 *  Uma lente é uma lista ordenada de regras. Cada regra diz o que
 *  apanha e o que faz com o que apanhou. **A primeira regra que casa
 *  ganha**: sem essa ordem, um elemento apanhado por duas regras ficava
 *  dependente de qual delas fosse avaliada por último, e o resultado
 *  mudava sem motivo visível.
 *
 *  O que fica de fora nesta versão, e está dito na interface: só se
 *  avalia o que o índice já traz (classe IFC, nome e modelo). Regras
 *  sobre property sets, quantidades e classificações obrigariam a ler
 *  os psets de todos os elementos, que é caro, e ficam para quando
 *  houver quem as peça.
 */

export type EixoLente = 'classe' | 'nome' | 'modelo'
export type OperadorLente = 'igual' | 'contem' | 'comeca' | 'vazio'
export type AccaoLente = 'colorir' | 'esconder' | 'transparente' | 'fantasma'

export interface RegraLente {
  id: string
  nome: string
  activa: boolean
  eixo: EixoLente
  operador: OperadorLente
  /** Ignorado quando o operador é 'vazio'. */
  valor: string
  accao: AccaoLente
  /** Só usada quando a acção é 'colorir'. Hexadecimal, com cardinal. */
  cor: string
}

export interface Lente {
  id: string
  nome: string
  regras: RegraLente[]
}

/** O mínimo que uma entrada precisa de ter para ser avaliada.
 *  Coincide de propósito com o índice da pesquisa: é o mesmo trabalho
 *  de leitura, feito uma vez. */
export interface EntradaLente {
  modelId: string
  localId: number
  ifcClass: string
  nome: string | null
}

export interface ResultadoLente {
  /** modelId -> localIds a pintar com aquela cor. */
  cores: Map<string, Map<string, number[]>>
  /** modelId -> localIds a esconder. */
  escondidos: Map<string, number[]>
  /** modelId -> localIds a pôr translúcidos. */
  transparentes: Map<string, number[]>
  /** modelId -> localIds em fantasma: só as arestas, esbatidas, para se
   *  ver o que está atrás (viewerApi.setFantasmaDaLente). */
  fantasmas: Map<string, number[]>
  /** Quantos elementos não casaram com regra nenhuma. */
  semRegra: number
  /** Quantos elementos casaram, por regra. Alimenta a legenda. */
  contagem: Record<string, number>
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

/** O valor de uma entrada no eixo pedido. */
export function valorNoEixo(
  entrada: EntradaLente,
  eixo: EixoLente,
): string | null {
  switch (eixo) {
    case 'classe':
      return entrada.ifcClass
    case 'nome':
      return entrada.nome
    case 'modelo':
      return entrada.modelId
  }
}

/** Uma regra apanha esta entrada? */
export function regraCasa(regra: RegraLente, entrada: EntradaLente): boolean {
  if (!regra.activa) return false
  const bruto = valorNoEixo(entrada, regra.eixo)

  if (regra.operador === 'vazio') {
    // "Sem nome" é uma pergunta legítima e frequente: um modelo bem
    // feito não tem elementos sem nome.
    return bruto === null || bruto.trim() === ''
  }
  if (bruto === null) return false

  const alvo = normalizar(bruto)
  const procura = normalizar(regra.valor)
  if (procura === '') return false

  switch (regra.operador) {
    case 'igual':
      return alvo === procura
    case 'comeca':
      return alvo.startsWith(procura)
    case 'contem':
      return alvo.includes(procura)
  }
}

/** A primeira regra que casa, ou null. */
export function primeiraRegraQueCasa(
  lente: Lente,
  entrada: EntradaLente,
): RegraLente | null {
  for (const regra of lente.regras) {
    if (regraCasa(regra, entrada)) return regra
  }
  return null
}

function acrescentar(
  mapa: Map<string, number[]>,
  chave: string,
  valor: number,
): void {
  const lista = mapa.get(chave)
  if (lista) lista.push(valor)
  else mapa.set(chave, [valor])
}

/** Avalia a lente sobre as entradas todas.
 *
 *  Devolve listas por modelo porque é assim que o motor de pintura e o
 *  de visibilidade as querem: uma chamada por modelo, não uma por
 *  elemento. */
export function avaliarLente(
  lente: Lente,
  entradas: readonly EntradaLente[],
): ResultadoLente {
  const cores = new Map<string, Map<string, number[]>>()
  const escondidos = new Map<string, number[]>()
  const transparentes = new Map<string, number[]>()
  const fantasmas = new Map<string, number[]>()
  const contagem: Record<string, number> = {}
  let semRegra = 0

  for (const entrada of entradas) {
    const regra = primeiraRegraQueCasa(lente, entrada)
    if (!regra) {
      semRegra += 1
      continue
    }
    contagem[regra.id] = (contagem[regra.id] ?? 0) + 1

    if (regra.accao === 'esconder') {
      acrescentar(escondidos, entrada.modelId, entrada.localId)
    } else if (regra.accao === 'transparente') {
      acrescentar(transparentes, entrada.modelId, entrada.localId)
    } else if (regra.accao === 'fantasma') {
      acrescentar(fantasmas, entrada.modelId, entrada.localId)
    } else {
      let porCor = cores.get(entrada.modelId)
      if (!porCor) {
        porCor = new Map()
        cores.set(entrada.modelId, porCor)
      }
      acrescentar(porCor, regra.cor, entrada.localId)
    }
  }

  return { cores, escondidos, transparentes, fantasmas, semRegra, contagem }
}

/** Paleta de arranque, tirada do branding bSPT.
 *
 *  O vermelho está de fora de propósito: na casa está reservado para
 *  dívida e erro, e uma lente que pinte paredes de vermelho por
 *  acidente faz alguém pensar que há um problema. */
export const CORES_LENTE = [
  '#004F9E',
  '#009DB0',
  '#9D358A',
  '#E9A13B',
  '#4C9A5A',
  '#7A6FB0',
  '#C2703D',
  '#5B7A8C',
]

export function corSeguinte(regras: readonly RegraLente[]): string {
  const usadas = new Set(regras.map((r) => r.cor))
  return CORES_LENTE.find((c) => !usadas.has(c)) ?? CORES_LENTE[0]
}

/** Uma lente que divide o modelo pelos valores distintos de um eixo.
 *
 *  É o atalho que substitui escrever dez regras à mão, e é o que torna
 *  isto útil na primeira utilização: escolhe-se o eixo e sai uma lente
 *  com uma cor por valor e uma legenda.
 *
 *  `limite` existe porque um modelo pode ter centenas de nomes
 *  distintos, e uma legenda com centenas de linhas não é uma legenda.
 *  Os valores mais frequentes ganham; o resto fica sem regra, em
 *  fantasma. */
export function lenteAutomatica(
  eixo: EixoLente,
  entradas: readonly EntradaLente[],
  limite = 12,
): Lente {
  const contagem = new Map<string, number>()
  for (const entrada of entradas) {
    const valor = valorNoEixo(entrada, eixo)
    if (valor === null || valor.trim() === '') continue
    contagem.set(valor, (contagem.get(valor) ?? 0) + 1)
  }
  const ordenados = [...contagem.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limite)

  const regras: RegraLente[] = ordenados.map(([valor], i) => ({
    id: `auto-${i}`,
    nome: valor,
    activa: true,
    eixo,
    operador: 'igual',
    valor,
    accao: 'colorir',
    cor: CORES_LENTE[i % CORES_LENTE.length],
  }))

  const nomeDoEixo = { classe: 'classe', nome: 'nome', modelo: 'modelo' }[eixo]
  return { id: `auto-${eixo}`, nome: `Por ${nomeDoEixo}`, regras }
}

export const LENTES_KEY = 'vn2r.lentes'

interface Armazenamento {
  getItem: (chave: string) => string | null
  setItem: (chave: string, valor: string) => void
}

function armazenamento(): Armazenamento | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** Uma lente lida de fora é dados, não código: valida-se campo a
 *  campo em vez de se confiar no que lá está. */
export function lenteValida(valor: unknown): valor is Lente {
  if (typeof valor !== 'object' || valor === null) return false
  const l = valor as Partial<Lente>
  if (typeof l.id !== 'string' || typeof l.nome !== 'string') return false
  if (!Array.isArray(l.regras)) return false
  return l.regras.every((r) => {
    if (typeof r !== 'object' || r === null) return false
    const x = r as Partial<RegraLente>
    return (
      typeof x.id === 'string' &&
      typeof x.nome === 'string' &&
      typeof x.activa === 'boolean' &&
      (x.eixo === 'classe' || x.eixo === 'nome' || x.eixo === 'modelo') &&
      (x.operador === 'igual' ||
        x.operador === 'contem' ||
        x.operador === 'comeca' ||
        x.operador === 'vazio') &&
      typeof x.valor === 'string' &&
      (x.accao === 'colorir' ||
        x.accao === 'esconder' ||
        x.accao === 'transparente' ||
        x.accao === 'fantasma') &&
      typeof x.cor === 'string'
    )
  })
}

export function lerLentes(
  store: Armazenamento | null = armazenamento(),
): Lente[] {
  try {
    const bruto = store?.getItem(LENTES_KEY)
    if (!bruto) return []
    const lido: unknown = JSON.parse(bruto)
    if (!Array.isArray(lido)) return []
    return lido.filter(lenteValida)
  } catch {
    return []
  }
}

export function gravarLentes(
  lentes: readonly Lente[],
  store: Armazenamento | null = armazenamento(),
): void {
  try {
    store?.setItem(LENTES_KEY, JSON.stringify(lentes))
  } catch {
    /* sem onde guardar: as lentes duram esta sessão */
  }
  for (const cb of ouvintesDasLentes) cb()
}

/** Quem usa as lentes guardadas fora do painel (as regras do estilo do
 *  corte) sabe quando mudam. O evento `storage` do browser só chega às
 *  outras abas. */
const ouvintesDasLentes = new Set<() => void>()

export function ouvirLentes(cb: () => void): () => void {
  ouvintesDasLentes.add(cb)
  return () => {
    ouvintesDasLentes.delete(cb)
  }
}
